import React, { memo, useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import interactionResponse from 'await-interaction-response';
import clsx from 'clsx';
import _isEqual from 'lodash/isEqual';

import { useFeatures } from '@atc/features';

import { Button } from 'reaxl';
import { sendClick } from 'reaxl-analytics';

import { clearAllFiltersClick } from '@/analytics/srp/srpSearchEvents';

import authDuck from '@/ducks/authDuck.js';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import srpResultsDuck from '@/ducks/srp/srpResultsDuck.js';
import userDuck from '@/ducks/userDuck.js';

import useSrpNavigation from '@/hooks/useSrpNavigation';

function InventoryClearFiltersComponent({
    collapsed = false,
    className,
}) {
    const {
        carbravo_entry_points: [enableCarBravoEntryPoints],
        filter_chips_srp: [enableFilterChips],
        personalization: [enablePersonalization],
        clear_experience: [isClearExperienceEnabled],
    } = useFeatures([
        'carbravo_entry_points',
        'filter_chips_srp',
        'personalization',
        'clear_experience',
    ]);

    const dispatch = useDispatch();
    const navigateToSrp = useSrpNavigation();

    const hasResults = useSelector(srpResultsDuck.selectors.hasActiveResults);
    const filtersValues = useSelector(srpFiltersDuck.selectors.getFiltersValues);

    const selectedFilterChips = useSelector(srpFiltersDuck.selectors.getSelectedFilterChips, _isEqual);
    const [activeFilterChips, setActiveFilterChips] = useState(selectedFilterChips);

    useEffect(() => {
        if (hasResults) {
            setActiveFilterChips(selectedFilterChips);
        }

    }, [hasResults, selectedFilterChips]);

    const onOptionClearAllChips = useCallback(async (event, clearFiltersLabel) => {
        const { value } = event.target;
        await interactionResponse();
        event.target.value = value;

        // update redux state
        let keys;
        keys = Object.keys(filtersValues);
        keys = keys.filter((filters) => filters !== 'searchRadius');
        dispatch(srpFiltersDuck.creators.clearFilters(keys));

        let query = isClearExperienceEnabled ? { experience: null } : {};

        if (enableCarBravoEntryPoints) {
            query = {
                ...query,
                carProduct: null,
            };
        }

        await navigateToSrp({
            filtersValues: query,
            resetPagination: true,
            isNewSearch: true,
        });

        sendClick(clearAllFiltersClick, event, {
            clearFiltersLabel,
        });
    }, [filtersValues, dispatch, isClearExperienceEnabled, navigateToSrp, enableCarBravoEntryPoints]);

    const clearFiltersLabel = 'Clear Filters';

    const isDesktopCarousel = !collapsed;

    const search = useSelector(userDuck.selectors.getSavedSearch);
    const isLoggedIn = useSelector(authDuck.selectors.isLoggedIn);
    const requestValuesWithoutSort = useSelector(srpFiltersDuck.selectors.getRequestValues, _isEqual);

    const isSearchSavable = !isLoggedIn || !(search.title && search.title.length);
    const hasSearchParams = !!(requestValuesWithoutSort && Object.keys(requestValuesWithoutSort).length);
    const showSaveSearchLink = enablePersonalization && hasSearchParams
        && isSearchSavable;

    const linkClasses = clsx({
        'display-flex text-nowrap align-self-center': isDesktopCarousel,
    }, className);

    const renderPipe = (showSaveSearchLink || collapsed) && (
        <span className="margin-horizontal-2">|</span>
    );

    return (enableFilterChips && activeFilterChips.length > 0) ? (
        <span className={linkClasses}>
            { renderPipe }
            <Button
                key="filterChipClearFilters"
                onClick={(event) => onOptionClearAllChips(event, clearFiltersLabel)}
                className="text-nowrap"
                bsStyle="text"
            >
                {clearFiltersLabel}
            </Button>
        </span>
    ) : null;
}

export const InventoryClearFilters = memo(InventoryClearFiltersComponent, _isEqual);

export default InventoryClearFilters;
